import { flatten, pick } from 'lodash';
import { isAxiosError } from 'axios';
import { ApiError, AppError, ErrorCode } from '@/types/ErrorCodes';
import { formatDistance } from 'date-fns';
import { BaseEntity } from '@/api/EntityApi';
import {
	ComponentRelationCondition,
	ComponentRelationType,
	Language,
	Province,
	RelationConditionOperator,
	Translations,
} from '@/types/types';
import { pl } from 'date-fns/locale';
import {
	IMAGE_MIME_TYPE,
	MS_WORD_MIME_TYPE,
	PDF_MIME_TYPE,
} from '@mantine/dropzone';
import { AttributeViewType } from '@/api/AttributeApi';
import {
	DimensionType,
	PriceRuleType,
	ProductTypeStatus,
} from '@/api/ProductTypeApi';
import { Client, Merchant, UserType } from '@/types/users';
import { ComplaintStatus } from '@/api/ComplaintApi';
import { OrderStatus } from '@/api/OrderApi';
import { ProductDimension } from '@/api/ProductApi';
import APP_CONFIG from '@/configs/appConfig';
import moment from 'moment';
import LANGUAGES from '@/configs/languages';

export const priceFormatter = new Intl.NumberFormat('PL-pl', {
	style: 'currency',
	currency: 'PLN',
	currencyDisplay: 'code',
});

const PASSWORD_REGEX = /^(?=\D*\d)[^ ]{6,}$/;

/**
 *
 * @param password one number and at least 8
 * @returns
 */
export function validatePassword(password: string) {
	return PASSWORD_REGEX.test(password);
}

export function randomDate(start: Date, end: Date) {
	return moment(
		start.getTime() + Math.random() * (end.getTime() - start.getTime())
	).toDate();
}

export function formatDateForApi(date: moment.MomentInput) {
	return moment(date).format('YYYY-MM-DD');
}

export function formatDateForPresentation(
	date: moment.MomentInput = new Date()
) {
	return moment(date).format('DD/MM/YYYY');
}

export function formatTimeForPresentation(
	date: moment.MomentInput = new Date()
) {
	return moment(date).format('HH:mm');
}

export function sortByDate(
	dateA: moment.MomentInput,
	dateB: moment.MomentInput,
	order: 'asc' | 'desc' = 'asc'
) {
	const first = moment(dateA).valueOf(),
		second = moment(dateB).valueOf();

	return order === 'asc' ? first - second : second - first;
}

export function sortByName(
	nameA = '',
	nameB = '',
	order: 'asc' | 'desc' = 'asc'
) {
	return order === 'asc'
		? nameA.localeCompare(nameB)
		: nameB.localeCompare(nameA);
}

export function incrementDate(date: moment.MomentInput) {
	return moment(date).add(1, 'day').toDate();
}

export function decrementDate(date: moment.MomentInput) {
	return moment(date).subtract(1, 'day').toDate();
}

export function handleUnload(e: BeforeUnloadEvent) {
	e.preventDefault();
}

export const ID_REGEX =
	/[a-f0-9]{8}-?[a-f0-9]{4}-?[a-f0-9]{4}-?[89ab][a-f0-9]{3}-?[a-f0-9]{12}/g;

export function getIdsFromText(text: string) {
	return [...new Set(flatten([...text.matchAll(ID_REGEX)]))];
}

export function fileTypeToHeader(fileType: 'csv' | 'xlsx' | 'pdf') {
	switch (fileType) {
		case 'xlsx':
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
		case 'csv':
			return 'text/csv';
		case 'pdf':
			return 'application/pdf';
		default:
			return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
	}
}

export function priceToFixed(price: number) {
	return price.toFixed(2);
}

export const formatPrice = (value = 0, opts?: Intl.NumberFormatOptions) => {
	const formatter = new Intl.NumberFormat('pl', {
		style: 'currency',
		currency: 'PLN',
		currencyDisplay: 'code',
		maximumFractionDigits: !value ? 0 : undefined,
		...opts,
	});

	return formatter.format(value);
};

export const isAppError = (error: unknown): error is AppError => {
	return (
		!!(error as AppError).status &&
		!!(error as AppError).stack &&
		!!(error as AppError).message
	);
};

export const getErrorCodes = (error: unknown) => {
	if (isAxiosError<ApiError>(error) && error.response)
		return Object.values(error.response.data.message);
	return [ErrorCode.GENERIC];
};

export const errorCodesToMessage = (errorCodes: ErrorCode[]) =>
	errorCodes.map((code) => `errors.${code}`).join(', ');

export const fontColorForBackground = (backgroundColorHex = '#ffffff') => {
	return [
		0.299 * parseInt(backgroundColorHex.slice(1, 3), 16),
		0.587 * parseInt(backgroundColorHex.slice(3, 5), 16),
		0.114 * parseInt(backgroundColorHex.slice(5, 7), 16),
	].reduce((a, b) => a + b) /
		255 >
		0.65
		? '#495057'
		: '#ffffff';
};

export const formatFullName = (firstName?: string, lastName?: string) => {
	if (firstName && lastName) return `${firstName} ${lastName}`;
	if (!firstName && !lastName) return `-`;
	return firstName || lastName;
};

export function formatDateFromNow(date: Date) {
	return formatDistance(date, new Date(), {
		locale: pl,
		includeSeconds: true,
		addSuffix: true,
	});
}

export function isDefaultLanguage(language: Language): language is 'PL' {
	return language === APP_CONFIG.DEFAULT_LANGUAGE;
}

export function fileSizeToMB(size: number | string) {
	return Math.round((Number(size) / 1_000_000 + Number.EPSILON) * 100) / 100;
}

export function extractId<TData extends BaseEntity>(entity: TData) {
	return pick(entity, 'id');
}

export function getFileSrc(path?: string) {
	if (!path) return '';
	return `${import.meta.env.VITE_API_URL_UPLOADS}${path}`;
}

export function formatExactDate(date: moment.MomentInput = new Date()) {
	return `${formatDateForPresentation(date)}, ${formatTimeForPresentation(
		date
	)}`;
}

export function getFileTypeIcon(mimeType: string) {
	if ((IMAGE_MIME_TYPE as string[]).includes(mimeType))
		return 'mdi mdi-image text-success';
	if ((PDF_MIME_TYPE as string[]).includes(mimeType))
		return 'mdi mdi-file-pdf text-danger';
	if ((MS_WORD_MIME_TYPE as string[]).includes(mimeType))
		return 'mdi mdi-microsoft-word text-primary';

	return 'mdi-file-excel-box-outline text-dark';
}

export function getProvinces() {
	return [
		Province.DOLNOSLASKIE,
		Province.KUJAWSKO_POMORSKIE,
		Province.LUBELSKIE,
		Province.LUBUSKIE,
		Province.LODZKIE,
		Province.MALOPOLSKIE,
		Province.MAZOWIECKIE,
		Province.OPOLSKIE,
		Province.PODKARPACKIE,
		Province.PODLASKIE,
		Province.POMORSKIE,
		Province.SLASKIE,
		Province.SWIETOKRZYSKIE,
		Province.WARMINSKO_MAZURSKIE,
		Province.WIELKOPOLSKIE,
		Province.ZACHODNIO_POMORSKIE,
	];
}

export function getAttributeViewTypeName(viewType: AttributeViewType) {
	switch (viewType) {
		case AttributeViewType.TILES:
			return 'Kafelki';
		case AttributeViewType.INPUT:
			return 'Pole tekstowe';
		case AttributeViewType.COLOR:
			return 'Kolor';
		case AttributeViewType.THINNING:
			return 'Przerzedzenie';
		default:
			return 'Nieznany';
	}
}

export function getDimensionConfigTypeName(type: DimensionType) {
	switch (type) {
		case DimensionType.AUTO:
			return 'Automatyczne';
		case DimensionType.MANUAL:
			return 'Manualne';
	}
}

export function getPriceRuleTypeName(type: PriceRuleType) {
	switch (type) {
		case PriceRuleType.FIXED:
			return 'Stałe';
		case PriceRuleType.PERCENTAGE:
			return 'Procentowo';
		case PriceRuleType.PER_SQUARE_METER:
			return 'Na metr kwadratowy';
	}
}

export function getRelationTypeName(type: ComponentRelationType) {
	switch (type) {
		case ComponentRelationType.ATTRIBUTE:
			return 'Atrybut';
		case ComponentRelationType.ATTRIBUTE_VALUE:
			return 'Wartość atrybutu';
		case ComponentRelationType.PRODUCT_PROPERTY:
			return 'Właściwość produktu';
	}
}

export function getComponentRelationPropertyName(
	propery: ComponentRelationCondition['property']
) {
	switch (propery) {
		case 'weight':
			return 'Waga';
		case 'surface':
			return 'Powierzchnia';
		default:
			return 'Nieznana';
	}
}

export function getPropertyOperatorName(operator: RelationConditionOperator) {
	switch (operator) {
		case RelationConditionOperator.EQ:
			return 'Równa się';
		case RelationConditionOperator.GTE:
			return 'Większa lub równa';
		case RelationConditionOperator.LTE:
			return 'Mniejsza lub równa';
	}
}

export function getOrderStatusName(status: OrderStatus) {
	switch (status) {
		case OrderStatus.PENDING:
			return {
				name: 'Oczekujące',
				color: 'warning',
			};
		case OrderStatus.PROCESSING:
			return {
				name: 'Przetwarzane',
				color: 'info',
			};
		case OrderStatus.COMPLETED:
			return {
				name: 'Zrealizowane',
				color: 'success',
			};
		case OrderStatus.CANCELLED:
			return {
				name: 'Anulowane',
				color: 'danger',
			};
	}
}

export const getDimensionText = (dimension: ProductDimension) => {
	const { width, height } = dimension;
	if (!width && !height) return '-';

	return `${width ? `szerokość: ${width} mm` : ''}${
		width && height ? ', ' : ''
	}${height ? `wysokość ${height} mm` : ''}`;
};

export function getProductTypeStatusName(status: ProductTypeStatus) {
	switch (status) {
		case ProductTypeStatus.ACTIVE:
			return { name: 'Aktywny', color: 'success' };
		case ProductTypeStatus.INACTIVE:
			return { name: 'Nieaktywny', color: 'danger' };
	}
}

export function isMerchant(client: Client | Merchant): client is Merchant {
	return client.userType === UserType.MERCHANT;
}

export function getComplaintStatusName(status: ComplaintStatus) {
	switch (status) {
		case ComplaintStatus.NEW:
			return {
				name: 'Nowa',
				color: 'info',
			};
		case ComplaintStatus.IN_PROGRESS:
			return {
				name: 'Przetwarzana',
				color: 'warning',
			};
		case ComplaintStatus.ACCEPTED:
			return {
				name: 'Uznana',
				color: 'success',
			};
		case ComplaintStatus.REJECTED:
			return {
				name: 'Nieuznana',
				color: 'danger',
			};
	}
}

export function getEmptyTranslationObject<K extends string>(
	keys: K[]
): Translations<Record<K, string>> {
	const translations = {} as Translations<Record<K, string>>;

	let language: Language;
	for (language in LANGUAGES) {
		if (language === APP_CONFIG.DEFAULT_LANGUAGE) continue;

		translations[language] = {} as Record<K, string>;

		for (const key of keys) {
			translations[language][key] = '';
		}
	}

	return translations;
}
